import { AccountRecordType, ProductFamily } from '../../../types/api';
import { isCurrentDateBetween } from './components/loan_application/utils/helpers';
import CalendarIcon from './components/pricing-calculator/CalendarIcon';
import StandardIcon from './components/pricing-calculator/StandardIcon';
import TaxCreditIcon from './components/pricing-calculator/TaxCreditIcon';
import FlexLoanIcon from './components/pricing-calculator/FlexLoanIcon';
// Landing page link slugs
export const TERMS_OF_USE_SLUG = 'terms-of-use';
export const PRIVACY_POLICY_SLUG = 'privacy-policy';
export const CONTACT_SLUG = 'contact';
export const ACCESSIBILITY_SLUG = 'accessibility-statement';

// API endpoint paths
export const CREATE_SCHEMA_URL = 'users/sign-up/';
export const LOGOUT_URL = 'auth/sign-out/';
export const SIGNIN_URL = 'auth/sign-in/';
export const REFRESH_TOKEN_URL = 'auth/token/refresh/';
export const SIGNUP_URL = 'auth/sign-up/';
export const VERIFY_URL = 'auth/verify/';
export const UPDATE_PASSWORD_URL = 'auth/update-password/';
export const RESET_PASSWORD_URL = 'auth/reset-password/';
export const CHECK_PASSWORD_RESET_URL = 'auth/check-password-reset-code/';
export const USER_URL = 'users/';
export const USER_ACCOUNT_URL = 'users/account';
export const INSTALLER_APPLICATIONS_URL = 'installers/applications/';
export const INSTALLER_BORROWERS_URL = 'installers/borrowers/';
export const INSTALLER_SUMMARY_URL = 'installers/summary/';
export const INSTALLER_PROJECT_SUMMARY_URL = 'installers/summary/projects';
export const USER_APPLICATIONS_URL = 'user/applications/';
export const USER_APPLICATIONS_DOCUMENT_ANALYZE_URL =
  'user/applications/required-document/:id/analyze';
export const BORROWER_DOCUMENTS_URL = 'user/documents/';
export const PLAID_CREATE_LINK_TOKEN_URL = 'plaid/create_link_token/';
export const PLAID_GENERATE_ASSET_REPORT_URL = 'plaid/generate_asset_report/';
export const PLAID_GET_ASSET_REPORT_URL = 'plaid/get_asset_report/';
export const PLAID_DELETE_ASSET_REPORT = 'plaid/delete_asset_report/';
export const PLAID_PROCESS_ASSET_REPORT_URL = 'plaid/process_asset_report/';
export const FUNDED_PROJECTS_URL = 'projects';
export const PORTAL_RESOURCES_URL = 'resources/';
export const PORTAL_RATES_URL = 'rates/';
export const PORTAL_TRAINING_VIDEOS_URL = 'training-videos/';

// App internal routes
export const INSTALLER_ACCOUNT = 'dashboard/installer/account/';
export const INSTALLER_DASHBOARD_PATH = 'dashboard/borrowers';
/**
 * `sessionStorage` key: set before navigating to `/dashboard/borrowers` so the Alerts tab is
 * selected on arrival (survives React Strict Mode remounts; cleared after the tab is applied).
 */
export const BORROWER_BROWSE_OPEN_ALERTS_TAB_SESSION_KEY =
  'sunstone:borrowerBrowseOpenAlertsTab';
/** React Router path to installer applicant details (see `routes.tsx` under `borrowers`). */
export const applicantDetailsPath = (applicationId: string) =>
  `/dashboard/borrowers/applications/${applicationId}/details`;
export const PROJECT_DETAILS_PATH = 'dashboard/projects/';
export const USER_SETTINGS_PATH = 'settings/profile';
export const TEAM_SETTINGS_PATH = 'settings/team';
export const DEBUG_SETTINGS_PATH = 'debug';
export const NEW_APPLICATION_PATH = 'new-applicant';
export const INSTALLER_DASHBOARD_FUNDED_PROJECTS_URL =
  'installer/dashboard/projects';
export const INSTALLER_DASHBOARD_PROJECTS_SUMMARY_URL =
  'installer/dashboard/projects/summary';
export const INSTALLER_DASHBOARD_APPLICATIONS_URL =
  'installer/dashboard/applications';
export const INSTALLER_DASHBOARD_APPLICATIONS_SUMMARY_URL =
  'installer/dashboard/applications/summary';
/** Loan Event Ledger — POST alerts for many application IDs (installer/consultant). */
export const INSTALLER_LEDGER_ALERTS_BATCH_URL =
  'installers/ledger/alerts/batch';
/** BFF: PATCH mark single alert read (installer/consultant); append `/read` in API helper. */
export const installerLedgerAlertReadPath = (alertId: string) =>
  `installers/ledger/alerts/${encodeURIComponent(alertId)}/read`;
/** Must match `MAX_APPLICATION_IDS_BATCH` in app/services/ledger/batch_alerts.py */
export const LEDGER_ALERTS_BATCH_MAX_APPLICATION_IDS = 500;
/** BFF: ledger timeline for a single application (installer/consultant). */
export const installerApplicationLedgerEventsPath = (applicationId: string) =>
  `installers/applications/${applicationId}/ledger/events`;

// Business logic constants
export const APP_EXTERNAL_STATUSES = {
  CREATED: 'Application Created',
  APP_CREATED_PENDING_CR_EXTERNAL: 'Application Created Pending CR External',
  IN_PROGRESS: 'Application In Progress',
  APPLICATION_PENDING_CHANGE_REQUEST_EXTERNAL:
    'Application Pending Change Request External',
  SUBMITTED: 'Application Submitted',
  LOAN_PACKAGE: 'Loan Package',
  COMPLETE: 'Complete',
  EXPIRED: 'Expired',
  LOAN_AGREEMENT_EXPIRED: 'Loan Agreement Expired',
  CLOSED: 'Closed',
} as const;

export const FP_EXTERNAL_STATUSES = {
  IN_PROGRESS: 'In Progress',
  PENDING_CHANGE_ORDER: 'Pending Change Order',
  FULLY_FUNDED: 'Fully Funded',
  CANCELED: 'Canceled',
  CLOSED: 'Closed',
} as const;

export enum EXTERNAL_ACTIONS {
  APPLICATION_NOT_STARTED = 'Application Not Started',
  APP_CREATED_PENDING_CR_EXTERNAL = 'Application Created Pending CR External', // This relates to the internal status of Application Created Pending CR
  CANCELED = 'Canceled',
  CLOSED = 'Closed',
  EXPIRED = 'Expired',
  APPLICATION_NOT_COMPLETE = 'Application Not Complete',
  APPLICATION_PENDING_CHANGE_REQUEST_EXTERNAL = 'Application Pending Change Request External', // This relates to the internal status of Application Pending Change Request
  LOAN_APPROVED = 'Loan Approved',
  LOAN_DECLINED = 'Loan Declined',
  LOAN_PROCESSING = 'Loan Processing',
  PENDING_CHANGE_REQUEST = 'Pending Change Request',
  PROJECT_DELAYED = 'Project Delayed',
  UW_IN_PROGRESS = 'UW In-Progress',
  APPROVED_WITH_CONDITIONS = 'Approved With Conditions',
  LOAN_PACKAGE_SENT = 'Loan Package Sent',
  WAITING_ON_BORROWER = 'Waiting on Borrower',
  PROVIDE_MISSING_INFORMATION = 'Provide Missing Information',
  MISSING_DOCUMENTS = 'Missing Documents',
  APPROVED = 'Approved',
  UNDERWRITING_REVIEW = 'Underwriting Review',
}

export const EXTERNAL_ACTION_ORDER = new Map<any, number>([
  [EXTERNAL_ACTIONS.APPLICATION_NOT_STARTED, 0],
  [EXTERNAL_ACTIONS.APPLICATION_NOT_COMPLETE, 1],
  [EXTERNAL_ACTIONS.WAITING_ON_BORROWER, 2],
  [EXTERNAL_ACTIONS.UW_IN_PROGRESS, 3],
  [EXTERNAL_ACTIONS.APPROVED_WITH_CONDITIONS, 4],
  [EXTERNAL_ACTIONS.LOAN_APPROVED, 5],
  [EXTERNAL_ACTIONS.LOAN_DECLINED, 6],
  [EXTERNAL_ACTIONS.LOAN_PROCESSING, 7],
  [EXTERNAL_ACTIONS.PENDING_CHANGE_REQUEST, 8],
  [EXTERNAL_ACTIONS.PROJECT_DELAYED, 9],
  [EXTERNAL_ACTIONS.LOAN_PACKAGE_SENT, 10],
  [EXTERNAL_ACTIONS.CANCELED, 11],
  [EXTERNAL_ACTIONS.CLOSED, 12],
  [EXTERNAL_ACTIONS.EXPIRED, 13],
]);

export const APP_INTERNAL_STATUSES = {
  APP_CREATED: 'Application Created',
  APP_PENDING: 'Application Pending',
  APP_CREATED_PENDING_CR: 'Application Created Pending CR',
  APP_PENDING_CHANGE_REQUEST: 'Application Pending Change Request',
  DOCS_PENDING: 'Documents Pending',
  PENDING_BORROWER: 'Pending Borrower',
  PENDING_INSTALLER: 'Pending Installer',
  APP_COMPLETE: 'Application Complete',
  UW_IN_PROGRESS: 'UW In-Progress',
  UW_COMPLETE: 'UW Complete',
  APPROVED_WITH_CONDITIONS: 'Approved With Conditions',
  UW_PENDING_COUNTEROFFER: 'UW Pending Counteroffer',
  COMPLIANCE_PENDING: 'Compliance Pending',
  COMPLIANCE_FAIL: 'Compliance Fail',
  COMPLIANCE_PASS: 'Compliance Pass',
  AGREEMENTS_PENDING: 'Agreements Pending',
  AGREEMENTS_EXECUTED: 'Agreements Executed',
  AGREEMENTS_COMPLETE: 'Agreement Complete',
  READY_TO_FUND: 'Ready to Fund',
  FUNDED: 'Funded',
  DECLINED: 'Declined',
  CLOSED: 'Closed',
  CANCELED_INSTALLER: 'Canceled: Installer',
  CANCELED_BORROWER: 'Canceled: Borrower',
  CANCELED_SUNSTONE: 'Canceled: Sunstone',
  CANCELED_NO_CONTACT: 'Canceled: Do Not Contact',
  CANCELED_EXPIRED: 'Canceled: Expired',
  DELAYED: 'Delayed',
  RE_UNDERWRITING_IN_PROGRESS: 'Re-Underwriting in Progress',
  RE_UNDERWRITING_DOCUMENTS_RECEIVED: 'Re-Underwriting Documents Received',
} as const;

export const WELCOME_PAGE_STATUSES = [
  APP_INTERNAL_STATUSES.APP_CREATED,
  APP_INTERNAL_STATUSES.APP_CREATED_PENDING_CR,
];

export type SYSTEM_POWER_USAGE_TYPE =
  | "Offset Borrower's utility bill"
  | 'Sold to another entity';

export const SYSTEM_POWER_USAGE_VALUES = {
  OFFSET_BORROWERS_UTILITY_BILL: "Offset Borrower's utility bill",
  SOLD_TO_ANOTHER_ENTITY: 'Sold to another entity',
};

export const PROJECT_MILESTONE = {
  NTP: 'Notice to Proceed',
  PO: 'Permits Obtained',
  EO: 'Equipment Order',
  RC: 'Racking Completion',
  MC: 'Mechanical Completion',
  SC: 'Substantial Completion',
  FC: 'Final Completion',
  RT: 'Rooftop',
  CT: 'Custom',
  FullyFunded: 'Fully Funded',
} as const;

export type MilestoneLabel =
  (typeof PROJECT_MILESTONE)[keyof typeof PROJECT_MILESTONE];

export const PROJECT_MILESTONE_ORDER = new Map<any, number>([
  [PROJECT_MILESTONE.NTP, 0],
  [PROJECT_MILESTONE.PO, 1],
  [PROJECT_MILESTONE.EO, 2],
  [PROJECT_MILESTONE.RC, 3],
  [PROJECT_MILESTONE.MC, 4],
  [PROJECT_MILESTONE.SC, 5],
  [PROJECT_MILESTONE.FC, 6],
  [PROJECT_MILESTONE.FullyFunded, 7],
]);

export enum MILESTONE_TYPES {
  NOTICE_TO_PROCEED = 'NTP',
  PERMITS_OBTAINED = 'PO',
  EQUIPMENT_ORDER = 'EO',
  RACKING_COMPLETION = 'RC',
  MECHANICAL_COMPLETION = 'MC',
  SUBSTANTIAL_COMPLETION = 'SC',
  FINAL_COMPLETION = 'FC',
  FULLY_FUNDED = 'FullyFunded',
}

/** Sort order for milestone.type short codes (NTP, EO, …) — used by Milestones tab + Project Summary overview. */
export const MILESTONE_TYPE_ORDER: Record<string, number> = {
  NTP: 0,
  PO: 0,
  EO: 1,
  RC: 2,
  MC: 3,
  SC: 4,
  FC: 5,
};

// 'Notice to Proceed' -> 'NTP', etc.
export const MILESTONE_LONG_TO_SHORT = {
  [PROJECT_MILESTONE.NTP]: MILESTONE_TYPES.NOTICE_TO_PROCEED,
  [PROJECT_MILESTONE.PO]: MILESTONE_TYPES.PERMITS_OBTAINED,
  [PROJECT_MILESTONE.EO]: MILESTONE_TYPES.EQUIPMENT_ORDER,
  [PROJECT_MILESTONE.RC]: MILESTONE_TYPES.RACKING_COMPLETION,
  [PROJECT_MILESTONE.MC]: MILESTONE_TYPES.MECHANICAL_COMPLETION,
  [PROJECT_MILESTONE.SC]: MILESTONE_TYPES.SUBSTANTIAL_COMPLETION,
  [PROJECT_MILESTONE.FC]: MILESTONE_TYPES.FINAL_COMPLETION,
  [PROJECT_MILESTONE.FullyFunded]: MILESTONE_TYPES.FULLY_FUNDED,
} as const;

export const MILESTONE_SHORT_TO_LONG = {
  [MILESTONE_TYPES.NOTICE_TO_PROCEED]: PROJECT_MILESTONE.NTP,
  [MILESTONE_TYPES.PERMITS_OBTAINED]: PROJECT_MILESTONE.PO,
  [MILESTONE_TYPES.EQUIPMENT_ORDER]: PROJECT_MILESTONE.EO,
  [MILESTONE_TYPES.RACKING_COMPLETION]: PROJECT_MILESTONE.RC,
  [MILESTONE_TYPES.MECHANICAL_COMPLETION]: PROJECT_MILESTONE.MC,
  [MILESTONE_TYPES.SUBSTANTIAL_COMPLETION]: PROJECT_MILESTONE.SC,
  [MILESTONE_TYPES.FINAL_COMPLETION]: PROJECT_MILESTONE.FC,
  [MILESTONE_TYPES.FULLY_FUNDED]: PROJECT_MILESTONE.FullyFunded,
} as const;

// LTT Task enums
export const APP_TASK_STATUS = {
  OPEN: 'OPEN',
  COMPLETED: 'COMPLETED',
} as const;

export const APP_TASK_TYPES = {
  TASK: 'Task',
  EMAIL: 'Email',
  LIST_EMAIL: 'List Email',
  CADENCE: 'Cadence',
  CALL: 'Call',
} as const;

export const USER_RECORD_TYPES = {
  BORROWER: 'borrower',
  INSTALLER: 'installer',
  PARTNER: 'partner',
} as const;

export const ACCOUNT_TYPES = {
  BORROWER: 'Borrower',
  CONSULTANT: 'Consultant',
  INSTALLER: 'Installer',
} as const;

export const INSTALLER_TYPES = {
  SALES: 'Sales',
  ADMIN: 'Admin',
  ACCOUNTING: 'Accounting',
} as const;

export const INSTALLER_TYPES_LABELS = {
  [INSTALLER_TYPES.SALES]: 'Sales',
  [INSTALLER_TYPES.ADMIN]: 'Admin',
  [INSTALLER_TYPES.ACCOUNTING]: 'Operations Manager',
} as const;

export const RELATED_PARTY_TYPES = {
  INDIVIDUAL: 'Individual',
  ENTITY: 'Entity',
} as const;

export const NUMBER_FORMATS = {
  ZIP: '#####',
  YEAR: '####',
  SSN: '###-##-####',
  PHONE: '(###) ###-####',
  EIN: '#########',
} as const;

export const PLACEHOLDERS = {
  PHONE: '(000) 000-0000',
  OPTIONAL: '(Optional)',
} as const;

export const MAX_NUMBER_INPUT_VALUE = 99999999999;

export const YES_NO = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

export const YES_NO_UPPERCASE = [
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
];

export const RENT_OWN = [
  { label: 'Rent', value: 'Rent' },
  { label: 'Own', value: 'Own' },
];

export enum OCCUPATION_TYPES {
  ACCOUNTANT = 'Accountant',
  ADMINISTRATIVE_SUPPORT = 'Administrative Support',
  ARCHITECT = 'Architect',
  BUSINESS_OWNER = 'Business Owner',
  CHEF = 'Chef',
  CONSTRUCTION_WORKER = 'Construction Worker',
  COUNSELOR = 'Counselor',
  DENTIST = 'Dentist',
  DOCTOR = 'Doctor',
  DRIVER = 'Driver',
  ENGINEER = 'Engineer',
  FARMER = 'Farmer',
  FINANCIAL_PROFESSIONAL = 'Financial Professional',
  FIREFIGHTER = 'Firefighter',
  FOOD_SERVER = 'Food Server',
  JANITORIAL_WORKER = 'Janitorial Worker',
  LAW_ENFORCEMENT = 'Law Enforcement',
  LAWYER = 'Lawyer',
  NURSE = 'Nurse',
  PERSONAL_CARE_AND_SERVICE_WORKER = 'Personal Care and Service Worker',
  PHARMACIST = 'Pharmacist',
  REAL_ESTATE_AGENT = 'Real Estate Agent',
  RELIGIOUS_WORKER = 'Religious Worker',
  SALES_REPRESENTATIVE = 'Sales Representative',
  SOFTWARE_DEVELOPER = 'Software Developer',
  TEACHER = 'Teacher',
  TECHNICIAN = 'Technician',
  THERAPIST = 'Therapist',
  VETERINARIAN = 'Veterinarian',
  OTHER = 'Other',
}

export const OCCUPATIONS = [
  { label: 'Accountant', value: OCCUPATION_TYPES.ACCOUNTANT },
  {
    label: 'Administrative Support',
    value: OCCUPATION_TYPES.ADMINISTRATIVE_SUPPORT,
  },
  { label: 'Architect', value: OCCUPATION_TYPES.ARCHITECT },
  { label: 'Business Owner', value: OCCUPATION_TYPES.BUSINESS_OWNER },
  { label: 'Chef', value: OCCUPATION_TYPES.CHEF },
  { label: 'Construction Worker', value: OCCUPATION_TYPES.CONSTRUCTION_WORKER },
  { label: 'Counselor', value: OCCUPATION_TYPES.COUNSELOR },
  { label: 'Dentist', value: OCCUPATION_TYPES.DENTIST },
  { label: 'Doctor', value: OCCUPATION_TYPES.DOCTOR },
  { label: 'Driver', value: OCCUPATION_TYPES.DRIVER },
  { label: 'Engineer', value: OCCUPATION_TYPES.ENGINEER },
  { label: 'Farmer', value: OCCUPATION_TYPES.FARMER },
  {
    label: 'Financial Professional',
    value: OCCUPATION_TYPES.FINANCIAL_PROFESSIONAL,
  },
  { label: 'Firefighter', value: OCCUPATION_TYPES.FIREFIGHTER },
  { label: 'Food Server', value: OCCUPATION_TYPES.FOOD_SERVER },
  { label: 'Janitorial Worker', value: OCCUPATION_TYPES.JANITORIAL_WORKER },
  { label: 'Law Enforcement', value: OCCUPATION_TYPES.LAW_ENFORCEMENT },
  { label: 'Lawyer', value: OCCUPATION_TYPES.LAWYER },
  { label: 'Nurse', value: OCCUPATION_TYPES.NURSE },
  {
    label: 'Personal Care and Service Worker',
    value: OCCUPATION_TYPES.PERSONAL_CARE_AND_SERVICE_WORKER,
  },
  { label: 'Pharmacist', value: OCCUPATION_TYPES.PHARMACIST },
  { label: 'Real Estate Agent', value: OCCUPATION_TYPES.REAL_ESTATE_AGENT },
  { label: 'Religious Worker', value: OCCUPATION_TYPES.RELIGIOUS_WORKER },
  {
    label: 'Sales Representative',
    value: OCCUPATION_TYPES.SALES_REPRESENTATIVE,
  },
  { label: 'Software Developer', value: OCCUPATION_TYPES.SOFTWARE_DEVELOPER },
  { label: 'Teacher', value: OCCUPATION_TYPES.TEACHER },
  { label: 'Technician', value: OCCUPATION_TYPES.TECHNICIAN },
  { label: 'Therapist', value: OCCUPATION_TYPES.THERAPIST },
  { label: 'Veterinarian', value: OCCUPATION_TYPES.VETERINARIAN },
  { label: 'Other', value: OCCUPATION_TYPES.OTHER },
];

export const SF_MAX_CHAR_LIMITS = {
  CHAR_FIELD: 255,
  TEXT_AREA: 1000,
  EMAIL: 80,
} as const;

export const UNITED_STATES = [
  { label: 'Alabama', value: 'Alabama', abbreviation: 'AL' },
  { label: 'Alaska', value: 'Alaska', abbreviation: 'AK' },
  { label: 'Arizona', value: 'Arizona', abbreviation: 'AZ' },
  { label: 'Arkansas', value: 'Arkansas', abbreviation: 'AR' },
  { label: 'California', value: 'California', abbreviation: 'CA' },
  { label: 'Colorado', value: 'Colorado', abbreviation: 'CO' },
  { label: 'Connecticut', value: 'Connecticut', abbreviation: 'CT' },
  { label: 'Delaware', value: 'Delaware', abbreviation: 'DE' },
  {
    label: 'District of Columbia (Washington, D.C.)',
    value: 'Washington, D.C.',
    abbreviation: 'DC',
  },
  { label: 'Florida', value: 'Florida', abbreviation: 'FL' },
  { label: 'Georgia', value: 'Georgia', abbreviation: 'GA' },
  { label: 'Hawaii', value: 'Hawaii', abbreviation: 'HI' },
  { label: 'Idaho', value: 'Idaho', abbreviation: 'ID' },
  { label: 'Illinois', value: 'Illinois', abbreviation: 'IL' },
  { label: 'Indiana', value: 'Indiana', abbreviation: 'IN' },
  { label: 'Iowa', value: 'Iowa', abbreviation: 'IA' },
  { label: 'Kansas', value: 'Kansas', abbreviation: 'KS' },
  { label: 'Kentucky', value: 'Kentucky', abbreviation: 'KY' },
  { label: 'Louisiana', value: 'Louisiana', abbreviation: 'LA' },
  { label: 'Maine', value: 'Maine', abbreviation: 'ME' },
  { label: 'Maryland', value: 'Maryland', abbreviation: 'MD' },
  { label: 'Massachusetts', value: 'Massachusetts', abbreviation: 'MA' },
  { label: 'Michigan', value: 'Michigan', abbreviation: 'MI' },
  { label: 'Minnesota', value: 'Minnesota', abbreviation: 'MN' },
  { label: 'Mississippi', value: 'Mississippi', abbreviation: 'MS' },
  { label: 'Missouri', value: 'Missouri', abbreviation: 'MO' },
  { label: 'Montana', value: 'Montana', abbreviation: 'MT' },
  { label: 'Nebraska', value: 'Nebraska', abbreviation: 'NE' },
  { label: 'Nevada', value: 'Nevada', abbreviation: 'NV' },
  { label: 'New Hampshire', value: 'New Hampshire', abbreviation: 'NH' },
  { label: 'New Jersey', value: 'New Jersey', abbreviation: 'NJ' },
  { label: 'New Mexico', value: 'New Mexico', abbreviation: 'NM' },
  { label: 'New York', value: 'New York', abbreviation: 'NY' },
  { label: 'North Carolina', value: 'North Carolina', abbreviation: 'NC' },
  { label: 'North Dakota', value: 'North Dakota', abbreviation: 'ND' },
  { label: 'Ohio', value: 'Ohio', abbreviation: 'OH' },
  { label: 'Oklahoma', value: 'Oklahoma', abbreviation: 'OK' },
  { label: 'Oregon', value: 'Oregon', abbreviation: 'OR' },
  { label: 'Pennsylvania', value: 'Pennsylvania', abbreviation: 'PA' },
  { label: 'Rhode Island', value: 'Rhode Island', abbreviation: 'RI' },
  { label: 'South Carolina', value: 'South Carolina', abbreviation: 'SC' },
  { label: 'South Dakota', value: 'South Dakota', abbreviation: 'SD' },
  { label: 'Tennessee', value: 'Tennessee', abbreviation: 'TN' },
  { label: 'Texas', value: 'Texas', abbreviation: 'TX' },
  { label: 'Utah', value: 'Utah', abbreviation: 'UT' },
  { label: 'Vermont', value: 'Vermont', abbreviation: 'VT' },
  { label: 'Virginia', value: 'Virginia', abbreviation: 'VA' },
  { label: 'Washington', value: 'Washington', abbreviation: 'WA' },
  { label: 'West Virginia', value: 'West Virginia', abbreviation: 'WV' },
  { label: 'Wisconsin', value: 'Wisconsin', abbreviation: 'WI' },
  { label: 'Wyoming', value: 'Wyoming', abbreviation: 'WY' },
];

export const maxFileSizeInBytes = 50 * 1024 * 1024; // 50MB

export const genericRequiredFieldError = 'This field is required';

export const COUNTRIES = [
  { name: 'Afghanistan', code: 'AF' },
  { name: 'Åland Islands', code: 'AX' },
  { name: 'Albania', code: 'AL' },
  { name: 'Algeria', code: 'DZ' },
  { name: 'American Samoa', code: 'AS' },
  { name: 'Andorra', code: 'AD' },
  { name: 'Angola', code: 'AO' },
  { name: 'Anguilla', code: 'AI' },
  { name: 'Antarctica', code: 'AQ' },
  { name: 'Antigua and Barbuda', code: 'AG' },
  { name: 'Argentina', code: 'AR' },
  { name: 'Armenia', code: 'AM' },
  { name: 'Aruba', code: 'AW' },
  { name: 'Australia', code: 'AU' },
  { name: 'Austria', code: 'AT' },
  { name: 'Azerbaijan', code: 'AZ' },
  { name: 'Bahamas', code: 'BS' },
  { name: 'Bahrain', code: 'BH' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Barbados', code: 'BB' },
  { name: 'Belarus', code: 'BY' },
  { name: 'Belgium', code: 'BE' },
  { name: 'Belize', code: 'BZ' },
  { name: 'Benin', code: 'BJ' },
  { name: 'Bermuda', code: 'BM' },
  { name: 'Bhutan', code: 'BT' },
  { name: 'Bolivia', code: 'BO' },
  { name: 'Bosnia and Herzegovina', code: 'BA' },
  { name: 'Botswana', code: 'BW' },
  { name: 'Bouvet Island', code: 'BV' },
  { name: 'Brazil', code: 'BR' },
  { name: 'British Indian Ocean Territory', code: 'IO' },
  { name: 'Brunei Darussalam', code: 'BN' },
  { name: 'Bulgaria', code: 'BG' },
  { name: 'Burkina Faso', code: 'BF' },
  { name: 'Burundi', code: 'BI' },
  { name: 'Cambodia', code: 'KH' },
  { name: 'Cameroon', code: 'CM' },
  { name: 'Canada', code: 'CA' },
  { name: 'Cape Verde', code: 'CV' },
  { name: 'Cayman Islands', code: 'KY' },
  { name: 'Central African Republic', code: 'CF' },
  { name: 'Chad', code: 'TD' },
  { name: 'Chile', code: 'CL' },
  { name: 'China', code: 'CN' },
  { name: 'Christmas Island', code: 'CX' },
  { name: 'Cocos (Keeling) Islands', code: 'CC' },
  { name: 'Colombia', code: 'CO' },
  { name: 'Comoros', code: 'KM' },
  { name: 'Congo', code: 'CG' },
  { name: 'Congo, The Democratic Republic of the', code: 'CD' },
  { name: 'Cook Islands', code: 'CK' },
  { name: 'Costa Rica', code: 'CR' },
  { name: "Cote D'Ivoire", code: 'CI' },
  { name: 'Croatia', code: 'HR' },
  { name: 'Cuba', code: 'CU' },
  { name: 'Cyprus', code: 'CY' },
  { name: 'Czech Republic', code: 'CZ' },
  { name: 'Denmark', code: 'DK' },
  { name: 'Djibouti', code: 'DJ' },
  { name: 'Dominica', code: 'DM' },
  { name: 'Dominican Republic', code: 'DO' },
  { name: 'Ecuador', code: 'EC' },
  { name: 'Egypt', code: 'EG' },
  { name: 'El Salvador', code: 'SV' },
  { name: 'Equatorial Guinea', code: 'GQ' },
  { name: 'Eritrea', code: 'ER' },
  { name: 'Estonia', code: 'EE' },
  { name: 'Ethiopia', code: 'ET' },
  { name: 'Falkland Islands (Malvinas)', code: 'FK' },
  { name: 'Faroe Islands', code: 'FO' },
  { name: 'Fiji', code: 'FJ' },
  { name: 'Finland', code: 'FI' },
  { name: 'France', code: 'FR' },
  { name: 'French Guiana', code: 'GF' },
  { name: 'French Polynesia', code: 'PF' },
  { name: 'French Southern Territories', code: 'TF' },
  { name: 'Gabon', code: 'GA' },
  { name: 'Gambia', code: 'GM' },
  { name: 'Georgia', code: 'GE' },
  { name: 'Germany', code: 'DE' },
  { name: 'Ghana', code: 'GH' },
  { name: 'Gibraltar', code: 'GI' },
  { name: 'Greece', code: 'GR' },
  { name: 'Greenland', code: 'GL' },
  { name: 'Grenada', code: 'GD' },
  { name: 'Guadeloupe', code: 'GP' },
  { name: 'Guam', code: 'GU' },
  { name: 'Guatemala', code: 'GT' },
  { name: 'Guernsey', code: 'GG' },
  { name: 'Guinea', code: 'GN' },
  { name: 'Guinea-Bissau', code: 'GW' },
  { name: 'Guyana', code: 'GY' },
  { name: 'Haiti', code: 'HT' },
  { name: 'Heard Island and Mcdonald Islands', code: 'HM' },
  { name: 'Holy See (Vatican City State)', code: 'VA' },
  { name: 'Honduras', code: 'HN' },
  { name: 'Hong Kong', code: 'HK' },
  { name: 'Hungary', code: 'HU' },
  { name: 'Iceland', code: 'IS' },
  { name: 'India', code: 'IN' },
  { name: 'Indonesia', code: 'ID' },
  { name: 'Iran, Islamic Republic Of', code: 'IR' },
  { name: 'Iraq', code: 'IQ' },
  { name: 'Ireland', code: 'IE' },
  { name: 'Isle of Man', code: 'IM' },
  { name: 'Israel', code: 'IL' },
  { name: 'Italy', code: 'IT' },
  { name: 'Jamaica', code: 'JM' },
  { name: 'Japan', code: 'JP' },
  { name: 'Jersey', code: 'JE' },
  { name: 'Jordan', code: 'JO' },
  { name: 'Kazakhstan', code: 'KZ' },
  { name: 'Kenya', code: 'KE' },
  { name: 'Kiribati', code: 'KI' },
  { name: "Korea, Democratic People'S Republic of", code: 'KP' },
  { name: 'Korea, Republic of', code: 'KR' },
  { name: 'Kuwait', code: 'KW' },
  { name: 'Kyrgyzstan', code: 'KG' },
  { name: "Lao People'S Democratic Republic", code: 'LA' },
  { name: 'Latvia', code: 'LV' },
  { name: 'Lebanon', code: 'LB' },
  { name: 'Lesotho', code: 'LS' },
  { name: 'Liberia', code: 'LR' },
  { name: 'Libyan Arab Jamahiriya', code: 'LY' },
  { name: 'Liechtenstein', code: 'LI' },
  { name: 'Lithuania', code: 'LT' },
  { name: 'Luxembourg', code: 'LU' },
  { name: 'Macao', code: 'MO' },
  { name: 'Macedonia, The Former Yugoslav Republic of', code: 'MK' },
  { name: 'Madagascar', code: 'MG' },
  { name: 'Malawi', code: 'MW' },
  { name: 'Malaysia', code: 'MY' },
  { name: 'Maldives', code: 'MV' },
  { name: 'Mali', code: 'ML' },
  { name: 'Malta', code: 'MT' },
  { name: 'Marshall Islands', code: 'MH' },
  { name: 'Martinique', code: 'MQ' },
  { name: 'Mauritania', code: 'MR' },
  { name: 'Mauritius', code: 'MU' },
  { name: 'Mayotte', code: 'YT' },
  { name: 'Mexico', code: 'MX' },
  { name: 'Micronesia, Federated States of', code: 'FM' },
  { name: 'Moldova, Republic of', code: 'MD' },
  { name: 'Monaco', code: 'MC' },
  { name: 'Mongolia', code: 'MN' },
  { name: 'Montserrat', code: 'MS' },
  { name: 'Morocco', code: 'MA' },
  { name: 'Mozambique', code: 'MZ' },
  { name: 'Myanmar', code: 'MM' },
  { name: 'Namibia', code: 'NA' },
  { name: 'Nauru', code: 'NR' },
  { name: 'Nepal', code: 'NP' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'Netherlands Antilles', code: 'AN' },
  { name: 'New Caledonia', code: 'NC' },
  { name: 'New Zealand', code: 'NZ' },
  { name: 'Nicaragua', code: 'NI' },
  { name: 'Niger', code: 'NE' },
  { name: 'Nigeria', code: 'NG' },
  { name: 'Niue', code: 'NU' },
  { name: 'Norfolk Island', code: 'NF' },
  { name: 'Northern Mariana Islands', code: 'MP' },
  { name: 'Norway', code: 'NO' },
  { name: 'Oman', code: 'OM' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Palau', code: 'PW' },
  { name: 'Palestinian Territory, Occupied', code: 'PS' },
  { name: 'Panama', code: 'PA' },
  { name: 'Papua New Guinea', code: 'PG' },
  { name: 'Paraguay', code: 'PY' },
  { name: 'Peru', code: 'PE' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Pitcairn', code: 'PN' },
  { name: 'Poland', code: 'PL' },
  { name: 'Portugal', code: 'PT' },
  { name: 'Puerto Rico', code: 'PR' },
  { name: 'Qatar', code: 'QA' },
  { name: 'Reunion', code: 'RE' },
  { name: 'Romania', code: 'RO' },
  { name: 'Russian Federation', code: 'RU' },
  { name: 'RWANDA', code: 'RW' },
  { name: 'Saint Helena', code: 'SH' },
  { name: 'Saint Kitts and Nevis', code: 'KN' },
  { name: 'Saint Lucia', code: 'LC' },
  { name: 'Saint Pierre and Miquelon', code: 'PM' },
  { name: 'Saint Vincent and the Grenadines', code: 'VC' },
  { name: 'Samoa', code: 'WS' },
  { name: 'San Marino', code: 'SM' },
  { name: 'Sao Tome and Principe', code: 'ST' },
  { name: 'Saudi Arabia', code: 'SA' },
  { name: 'Senegal', code: 'SN' },
  { name: 'Serbia and Montenegro', code: 'CS' },
  { name: 'Seychelles', code: 'SC' },
  { name: 'Sierra Leone', code: 'SL' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Slovakia', code: 'SK' },
  { name: 'Slovenia', code: 'SI' },
  { name: 'Solomon Islands', code: 'SB' },
  { name: 'Somalia', code: 'SO' },
  { name: 'South Africa', code: 'ZA' },
  { name: 'South Georgia and the South Sandwich Islands', code: 'GS' },
  { name: 'Spain', code: 'ES' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Sudan', code: 'SD' },
  { name: 'Suriname', code: 'SR' },
  { name: 'Svalbard and Jan Mayen', code: 'SJ' },
  { name: 'Swaziland', code: 'SZ' },
  { name: 'Sweden', code: 'SE' },
  { name: 'Switzerland', code: 'CH' },
  { name: 'Syrian Arab Republic', code: 'SY' },
  { name: 'Taiwan, Province of China', code: 'TW' },
  { name: 'Tajikistan', code: 'TJ' },
  { name: 'Tanzania, United Republic of', code: 'TZ' },
  { name: 'Thailand', code: 'TH' },
  { name: 'Timor-Leste', code: 'TL' },
  { name: 'Togo', code: 'TG' },
  { name: 'Tokelau', code: 'TK' },
  { name: 'Tonga', code: 'TO' },
  { name: 'Trinidad and Tobago', code: 'TT' },
  { name: 'Tunisia', code: 'TN' },
  { name: 'Turkey', code: 'TR' },
  { name: 'Turkmenistan', code: 'TM' },
  { name: 'Turks and Caicos Islands', code: 'TC' },
  { name: 'Tuvalu', code: 'TV' },
  { name: 'Uganda', code: 'UG' },
  { name: 'Ukraine', code: 'UA' },
  { name: 'United Arab Emirates', code: 'AE' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'United States', code: 'US' },
  { name: 'United States Minor Outlying Islands', code: 'UM' },
  { name: 'Uruguay', code: 'UY' },
  { name: 'Uzbekistan', code: 'UZ' },
  { name: 'Vanuatu', code: 'VU' },
  { name: 'Venezuela', code: 'VE' },
  { name: 'Viet Nam', code: 'VN' },
  { name: 'Virgin Islands, British', code: 'VG' },
  { name: 'Virgin Islands, U.S.', code: 'VI' },
  { name: 'Wallis and Futuna', code: 'WF' },
  { name: 'Western Sahara', code: 'EH' },
  { name: 'Yemen', code: 'YE' },
  { name: 'Zambia', code: 'ZM' },
  { name: 'Zimbabwe', code: 'ZW' },
];

export const CO_UTILITIES = [
  { label: 'Xcel Energy', value: 'Xcel Energy', abbreviation: null },
  { label: 'Other', value: 'Other', abbreviation: null },
];

export const IL_UTILITIES = [
  { label: 'Ameren Illinois', value: 'Ameren Illinois', abbreviation: null },
  {
    label: 'Commonwealth Edison',
    value: 'Commonwealth Edison',
    abbreviation: null,
  },
  {
    label: 'MidAmerican Energy',
    value: 'MidAmerican Energy',
    abbreviation: null,
  },
  { label: 'Mt. Carmel', value: 'Mt. Carmel', abbreviation: null },
  { label: 'Other (MISO)', value: 'Other (MISO)', abbreviation: null },
  { label: 'Other (PJM)', value: 'Other (PJM)  ', abbreviation: null },
];

export const MA_TERRITORIES = [
  { label: 'Eversource East', value: 'Eversource East', abbreviation: null },
  { label: 'Eversource West', value: 'Eversource West', abbreviation: null },
  {
    label: 'National Grid (Massachusetts Electric)',
    value: 'National Grid (Massachusetts Electric)',
    abbreviation: null,
  },
  {
    label: 'National Grid (Nantucket)',
    value: 'National Grid (Nantucket)',
    abbreviation: null,
  },
  { label: 'Unitil', value: 'Unitil', abbreviation: null },
  { label: 'Other', value: 'Other', abbreviation: null },
];

export const DSCR_AVG_LIMIT_1 = 1;
export const DSCR_AVG_LIMIT_2 = 0.7;
export const DSCR_MIN_LIMIT_1 = 0.8;
export const DSCR_MIN_LIMIT_2 = 0.5;

export const EXPIRATION_WARNING_CHANGE_DATE = new Date('2023-10-28T00:00:00Z');

export const APPLICATION_EXTENSION_LENGTH_IN_DAYS = 10;

export const FIN_OPS_EMAIL = 'finops@sunstonecredit.com';
export const UNDERWRITING_EMAIL = 'underwriting@sunstonecredit.com';

export const MILLISECONDS_IN_A_DAY = 86400000;

export const PG_PERCENT_OWNERSHIP_FLOOR = 51;
export const YEARS_BUSINESS_ESTABLISHED_FLOOR = 3;

export const AXIOS_TIMEOUT_MS = 180000;

export const TYPE_OF_ADVANCE_FUNDING = {
  GREENTECH_DIRECT_PAY: 'Greentech Direct Pay',
} as const;

export const INSTALLER_PROGRAM_STATUSES = {
  APPLICATION_SUBMITTED: 'Application Submitted',
  APPLICATION_IN_PROGRESS: 'Application In-Progress',
  APPLICATION_INCOMPLETE: 'Application Incomplete',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
};

export const INSTALLER_PROGRAM_APPROVED_STATUSES = [
  INSTALLER_PROGRAM_STATUSES.APPROVED,
];

export const INSTALLER_PROGRAM_TYPES = {
  MILESTONE_PLUS: 'Milestone Plus',
  MILESTONE_FULL_APPROVAL: 'Milestone Full Approval',
  MILESTONE_MANAGED_APPROVAL: 'Milestone Managed Approval',
  MILESTONE_DEAL_APPROVAL: 'Milestone Deal Approval',

  // @deprecated progam type from SF. DO NOT USE.
  MILESTONE_PROGRAM: 'Milestone Program',
  // @deprecated progam type from SF. DO NOT USE.
  GREENTECH_DIRECT_PAY_PROGRAM: 'Greentech Direct Pay Program',
  FLEX_MILESTONE_PLUS: 'Flex Milestone Plus',
} as const;

// All milestone program types for consistent checking
export const MILESTONE_PROGRAM_TYPES = [
  INSTALLER_PROGRAM_TYPES.MILESTONE_PLUS,
  INSTALLER_PROGRAM_TYPES.MILESTONE_FULL_APPROVAL,
  INSTALLER_PROGRAM_TYPES.MILESTONE_MANAGED_APPROVAL,
  INSTALLER_PROGRAM_TYPES.MILESTONE_DEAL_APPROVAL,
  // Include deprecated type to preserve legacy behavior
  INSTALLER_PROGRAM_TYPES.MILESTONE_PROGRAM,
  INSTALLER_PROGRAM_TYPES.FLEX_MILESTONE_PLUS,
] as const;

// Program types that will allow the Milestone Calculator to be displayed
export const MILESTONE_CALCULATOR_ALLOWED_PROGRAM_TYPES = [
  INSTALLER_PROGRAM_TYPES.MILESTONE_PLUS,
  INSTALLER_PROGRAM_TYPES.MILESTONE_FULL_APPROVAL,
  INSTALLER_PROGRAM_TYPES.MILESTONE_MANAGED_APPROVAL,
  INSTALLER_PROGRAM_TYPES.MILESTONE_DEAL_APPROVAL,
] as const;

// Helper function to check if a program type is a milestone program
export const isMilestoneProgram = (programType: string): boolean => {
  return MILESTONE_PROGRAM_TYPES.includes(programType as any);
};

export const FP_ADVANCE_FUNDING_TYPES = {
  MILESTONE_PROGRAM: 'Milestone Program',
  GREENTECH_DIRECT_PAY_PROGRAM: 'Greentech Direct Pay Program',
  EQUIPMENT_ADVANCE: 'Equipment Advance',
  PROJECT_ADVANCE: 'Project Advance',
} as const;

export const MILESTONE_STATUSES = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  PAID: 'Paid',
  WAIVED: 'Waived',
} as const;

export const ADVANCE_FUNDING_MILESTONES = ['PO', 'EO', 'MC', 'RC'];

export const FUNDING_LIMIT_WARNING_PERCENTAGE = 25;

export enum COLORS {
  GRAY = '#888888',
  LIGHT_GRAY = '#DDDDDD',
  MILESTONE_GREEN = '#2E9C7F',
  MILESTONE_GREEN_BACKGROUND = '#E3EEEA',
  WARNING_ORANGE = '#FB9906',
  WARNING_RED = '#E95857',
}

// The string to display to the borrower for each of these RD statuses defined in api.ts
export enum REQUIRED_DOCUMENT_APPROVAL_STATUSES_MAP {
  VERIFIED = 'Verified',
  MISSING = 'Missing',
  ISSUES_IDENTIFIED = 'Issues Identified',
  NEEDS_REVIEW = 'Under Review',
  SENT_TO_BANK = 'Sent to Bank',
  AWAITING_UPLOAD = 'Awaiting Upload',
}

export enum SUNSTONE_DOCUMENT_APPROVAL_STATUSES {
  APPROVED = 'Approved',
  AWAITING_UPLOAD = 'Awaiting Upload',
  ISSUES_IDENTIFIED = 'Issues Identified',
  PENDING_CALL = 'Pending Call',
  SENT_VIA_DOCUSIGN = 'Sent via Docusign',
  SIGNATURE_NEEDED = 'Signature Needed',
  WAITING_REVIEW = 'Waiting Review',
  WAIVED = 'Waived',
}

export enum LIMITS {
  MIN_YEAR_ALLOWED = 1637,
  MAX_YEARS_ESTABLISHED = 400,
  PG_LOAN_AMOUNT_CEILING = 350_000,
  EXPRESS_PLUS_LOAN_AMOUNT_FLOOR = PG_LOAN_AMOUNT_CEILING,
  EXPRESS_PLUS_LOAN_AMOUNT_CEILING = 750_000,
  EXPRESS_PLUS_REVENUE_CEILING = 10000000,
  EXPRESS_REVENUE_CEILING = 5000000,
  YEARS_AGO_ESTABLISHED_FLOOR = 5,
  PPA_LOAN_AMOUNT_FLOOR = 250000,
  PG_INDUSTRY_LOAN_AMOUNT_FLOOR = 750000,
  MIN_LOAN_AMOUNT = 50_000,
  MIN_FLEX_LOAN_AMOUNT = 25_000,
  MAX_LOAN_AMOUNT = 35_000_000,
}

export const COMPANY_TYPES_OBJECT_ARRAY = [
  { label: 'Operating Company', value: 'Operating Company' },
  { label: 'Property Company', value: 'Property Company' },
  { label: 'Agriculture', value: 'Agriculture' },
  { label: 'Non-Profit', value: 'Non-Profit' },
  { label: 'HOA', value: 'HOA' },
];

export enum COMPANY_TYPES {
  OPERATING_COMPANY = 'Operating Company',
  PROPERTY_COMPANY = 'Property Company',
  AGRICULTURE = 'Agriculture',
  NON_PROFIT = 'Non-Profit',
  HOA = 'HOA',
}

export const PG_COMPANY_TYPES = [
  COMPANY_TYPES.OPERATING_COMPANY,
  COMPANY_TYPES.PROPERTY_COMPANY,
  COMPANY_TYPES.AGRICULTURE,
];

export const SPECIAL_PAYMENT_REQUEST_OPTIONS = [
  { label: '30%', value: 30 },
  { label: '40%', value: 40 },
  { label: '50%', value: 50 },
];

export const SPECIAL_PAYMENT_REQUEST_OPTIONS_WITH_20 = [
  { label: '20%', value: 20 },
  ...SPECIAL_PAYMENT_REQUEST_OPTIONS,
];

export const TAX_CREDIT_REQUEST_OPTIONS = [{ label: '25%', value: 25 }];

export const CALCULATOR_TOOLTIPS = (companyName?: string) => ({
  MONTHLY_PAYMENT:
    'The estimated monthly payment amount is determined by the Loan Amount, Loan Type, Loan Term and Interest Rate.',
  NET_FUNDING: `The amount ${companyName} will receive from Sunstone after dealer fees. This number assumes 90%/10% payment terms (i.e., all payments are made after Substantial Completion) and no additional discounts or fees apply.`,
  TOTAL_LOAN_AMOUNT:
    "The amount that will appear on the customer's loan documents.",
  SPECIAL_PAYMENT_REQUEST:
    'Larger Special Payments result in lower monthly payments. The Special Payment is due by Month 18. Typically the 40% and 50% Special Payments are associated with ITC adders (eg Domestic content) or a REAP grant.',
});

export const PRODUCT_FAMILY_CONFIG = {
  [ProductFamily.STANDARD]: {
    label: 'Standard Loan',
    description:
      'Fixed monthly payments. Tend to have higher interest rates and monthly payments.',
    icon: StandardIcon,
    bulletPoints: [
      'Same monthly payment for the life of the loan',
      'No additional payments outside of the monthly payment',
      'Generally a larger monthly payment in comparison to the Special Payment Product',
    ],
  },
  [ProductFamily.SPECIAL_PAYMENT]: {
    label: 'Special Payment',
    description:
      'Fixed monthly payments and a special payment due in month 18. Tend to have lower interest rates and monthly payments.',
    icon: CalendarIcon,
    bulletPoints: [
      'Mandatory one-time special payment by month 18',
      'Generally smaller monthly payments due to the special payment',
      'Often used when there is certainty of the customer to receive incentives like ITC, REAP, etc',
    ],
  },
  [ProductFamily.PREMIUM]: {
    label: 'Premium',
    description: '',
    icon: undefined,
    bulletPoints: [],
  },
  [ProductFamily.TAX_CREDIT]: {
    label: 'Tax Credit Transfer',
    description:
      'Fixed monthly payments with a one-time special payment, typically covered by tax credits like the Federal ITC.',
    icon: TaxCreditIcon,
    bulletPoints: [
      'Lower monthly payments due to the deferred special payment',
      'One-time lump sum due in month 18, aligned with expected receipt of tax incentives',
      'Commonly used when the customer qualifies for federal or state tax credits (e.g., ITC, REAP)',
      'Helps bridge the gap between project cost and tax credit reimbursement',
    ],
  },
  [ProductFamily.FLEX]: {
    label: 'Flex',
    description:
      'Flexible monthly payments, designed for non-solar type of projects.',
    icon: FlexLoanIcon,
    bulletPoints: [],
  },
};

export enum Z_INDEXES {
  LOAN_PRODUCT_SUMMARY_BAR = 1200,
  ANNOUNCEMENTS_TAB_DESKTOP = 1101,
  ANNOUNCEMENTS_ICON_DESKTOP = 1300,
  ANNOUNCEMENTS_TAB_MOBILE = 1999,
  ANNOUNCEMENTS_ICON_MOBILE = 700,
  MODAL = 2999,
  ACCOUNT_POPOVER = 9999,
  NAVBAR = 1100,
  APPLICATION_ASIDE = 1099,
  MOBILE_NAVBAR = 2000,
  SPLASH_SCREEN = 2000,
  REVIEW_BUTTON = 1,
  TOOLTIP_POPOVER = 999,
}

export const INTERVAL_DATES = () => {
  const currentYear = new Date().getFullYear(); // Some Edge Case with local TZ
  return {
    JanMidFeb: isCurrentDateBetween(
      `${currentYear}-01-01 00:00:00`,
      `${currentYear}-02-15 23:59:59`
    ),
    MidFebMidApril: isCurrentDateBetween(
      `${currentYear}-02-16 00:00:00`,
      `${currentYear}-04-17 23:59:59`
    ),
    MidAprilMidMay: isCurrentDateBetween(
      `${currentYear}-04-18 00:00:00`,
      `${currentYear}-05-15 23:59:59`
    ),
    MidMayMidAug: isCurrentDateBetween(
      `${currentYear}-05-16 00:00:00`,
      `${currentYear}-08-14 23:59:59`
    ),
    MidAugMidOct: isCurrentDateBetween(
      `${currentYear}-08-15 00:00:00`,
      `${currentYear}-10-17 23:59:59`
    ),
    MidOctMidNov: isCurrentDateBetween(
      `${currentYear}-10-18 00:00:00`,
      `${currentYear}-11-14 23:59:59`
    ),
    // old dates
    JanApr: isCurrentDateBetween(
      `${currentYear}-01-01 00:00:00`,
      `${currentYear}-04-30 23:59:59`
    ),
    MayOct: isCurrentDateBetween(
      `${currentYear}-05-01 00:00:00`,
      `${currentYear}-10-31 23:59:59`
    ),
    NovDec: isCurrentDateBetween(
      `${currentYear}-11-01 00:00:00`,
      `${currentYear}-12-31 23:59:59`
    ),
    MidMaytoMidAug: isCurrentDateBetween(
      `${currentYear}-05-15 00:00:00`,
      `${currentYear}-08-14 23:59:59`
    ),
    MidAugtoMidNov: isCurrentDateBetween(
      `${currentYear}-08-15 00:00:00`,
      `${currentYear}-11-14 23:59:59`
    ),
    MidNovDec: isCurrentDateBetween(
      `${currentYear}-11-15 00:00:00`,
      `${currentYear}-12-31 23:59:59`
    ),
  };
};

export enum BORROWER_SUCCESS_CONTACT_INFO {
  NAME = 'Borrower Success',
  EMAIL = 'borrowersuccess@sunstonecredit.com',
  PHONE = '(800)-383-4910',
}

export enum PARTNER_SUCCESS_CONTACT_INFO {
  NAME = 'Partner Success Team',
  EMAIL = 'partnersuccess@sunstonecredit.com',
}

export const OUTSTANDING_LOAN_TYPES = [
  { label: 'Term Loan', value: 'Term Loan' },
  { label: 'Line of Credit', value: 'Line of Credit' },
  { label: 'SBA Loan', value: 'SBA Loan' },
  {
    label: 'Commercial Real Estate Loan',
    value: 'Commercial Real Estate Loan',
  },
  { label: 'Equipment Financing', value: 'Equipment Financing' },
  { label: 'Auto Loan', value: 'Auto Loan' },
  { label: 'Construction Loan', value: 'Construction Loan' },
  { label: 'Mortgage', value: 'Mortgage' },
  { label: 'Shareholder Loan', value: 'Shareholder Loan' },
  { label: 'COVID-related Loan', value: 'COVID-related Loan' },
  { label: 'Other', value: 'Other' },
];

export const INSTALLER_DOCUMENT_TYPE = {
  SOLAR_LOAN_PROPOSAL: 'Solar Loan Proposal',
  PROJECT_PROPOSAL: 'Project Proposal',
  UTILITY_BILLS: 'Utility Bills',
  INCENTIVE_DOCUMENTS: 'Incentive Documents',
  NEW_SOLAR_PROPOSAL: 'New Solar Proposal',
  MILESTONE_FUNDING_DOCUMENT: 'Installer Milestone-Funding Document',
};

// Solar/+ uploads as SOLAR_LOAN_PROPOSAL, Flex-only as PROJECT_PROPOSAL
// (per-type BE Oracle extraction). Display consumers accept either.
export const PROPOSAL_DOC_TYPES: ReadonlyArray<string> = [
  INSTALLER_DOCUMENT_TYPE.PROJECT_PROPOSAL,
  INSTALLER_DOCUMENT_TYPE.SOLAR_LOAN_PROPOSAL,
];

export enum REQUIRED_DOCUMENT_SUB_TYPES {
  PG = 'PG',
  CG = 'CG',
  BORROWER = 'BORROWER',
}

export enum APPLICATION_BORROWER_BUSINESS_STRUCTURE {
  SOLE_PROPRIETORSHIP = 'SOLE PROPRIETORSHIP',
  NON_PROFIT = 'NON-PROFIT',
  HOA = 'HOA',
  SCORP = 'S-CORP',
  CCORP = 'C-CORP',
  LLC = 'LLC',
  LLP = 'LLP',
  GENERAL_PARTNERSHIP = 'GENERAL PARTNERSHIP',
  IRREVOCABLE_TRUST = 'Irrevocable Trust',
  REVOCABLE_TRUST = 'Revocable Trust',
}

export enum MARKET_ESTIMATION_VALUE_METHOD {
  SELF_REPORTED = 'Self Reported',
  TAX_ASSESSED_VALUE = 'Tax Assessed Value',
  RECENT_PROPERTY_SALE = 'Recent Property Sale',
  RECENT_APPRAISAL = 'Recent Appraisal',
}

export const MARKET_ESTIMATION_VALUE_CHOICES = [
  {
    label: MARKET_ESTIMATION_VALUE_METHOD.SELF_REPORTED,
    value: MARKET_ESTIMATION_VALUE_METHOD.SELF_REPORTED,
  },
  {
    label: MARKET_ESTIMATION_VALUE_METHOD.TAX_ASSESSED_VALUE,
    value: MARKET_ESTIMATION_VALUE_METHOD.TAX_ASSESSED_VALUE,
  },
  {
    label: MARKET_ESTIMATION_VALUE_METHOD.RECENT_PROPERTY_SALE,
    value: MARKET_ESTIMATION_VALUE_METHOD.RECENT_PROPERTY_SALE,
  },
  {
    label: MARKET_ESTIMATION_VALUE_METHOD.RECENT_APPRAISAL,
    value: MARKET_ESTIMATION_VALUE_METHOD.RECENT_APPRAISAL,
  },
];

export enum SYSTEM_PROPERTY_RECORD_TYPE {
  ANOTHER_INDIVIDUAL = 'Another Individual',
  ANOTHER_BUSINESS = 'Another Business',
}

export const SYSTEM_PROPERTY_RECORD_TYPE_CHOICES = [
  {
    label: SYSTEM_PROPERTY_RECORD_TYPE.ANOTHER_INDIVIDUAL,
    value: RELATED_PARTY_TYPES.INDIVIDUAL,
  },
  {
    label: SYSTEM_PROPERTY_RECORD_TYPE.ANOTHER_BUSINESS,
    value: RELATED_PARTY_TYPES.ENTITY,
  },
];

export enum MULTIPLE_USER_POWER_AGREEMENT {
  PPA_LEASE = 'PPA/Lease Agreement',
  COMMUNITY_SOLAR = 'Community Solar Arrangement',
}

export const REQUIRED_DOCUMENT_REQUIRED_FOR_STEP = {
  BORROWER_APP_SUBMISSION: 'BORROWER_APP_SUBMISSION',
  UNDERWRITING_APPROVAL: 'UNDERWRITING_APPROVAL',
  BORROWER_SIGNING: 'BORROWER_SIGNING',
  PROJECT_START: 'PROJECT_START',
  NOTICE_TO_PROCEED: 'NOTICE_TO_PROCEED',
  PERMITS_OBTAINED: 'PERMITS_OBTAINED',
  EQUIPMENT_ORDER: 'EQUIPMENT_ORDER',
  RACKING_COMPLETION: 'RACKING_COMPLETION',
  MECHANICAL_COMPLETION: 'MECHANICAL_COMPLETION',
  SUBSTANTIAL_COMPLETION: 'SUBSTANTIAL_COMPLETION',
  FINAL_COMPLETION: 'FINAL_COMPLETION',
  FULLY_FUNDED: 'FULLY_FUNDED',
} as const;

// Mapping of required for step values from SF Sunstone Document to the values used in Postgres required_documents
export const SF_REQUIRED_FOR_STEP_MAPPING = {
  'Borrower App Submission':
    REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.BORROWER_APP_SUBMISSION,
  'Underwriting Approval':
    REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.UNDERWRITING_APPROVAL,
  'Borrower Signing': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.BORROWER_SIGNING,
  'Project Start': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.PROJECT_START,
  'Notice to Proceed': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.NOTICE_TO_PROCEED,
  'Permits Obtained': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.PERMITS_OBTAINED,
  'Equipment Order': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.EQUIPMENT_ORDER,
  'Racking Completion': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.RACKING_COMPLETION,
  'Mechanical Completion':
    REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.MECHANICAL_COMPLETION,
  'Substantial Completion':
    REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.SUBSTANTIAL_COMPLETION,
  'Final Completion': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.FINAL_COMPLETION,
  'Fully Funded': REQUIRED_DOCUMENT_REQUIRED_FOR_STEP.FULLY_FUNDED,
} as const;

export const MINIMUM_LOAN_BALANCE = 10000;
export const defaultApplicantName = 'Applicant';
export const DEFAULT_PROPERTY_OWNER_NAME = 'the property owner';

export enum FOR_PROFIT_COMPANY_TYPES {
  SELLS_GOODS_OR_SERVICES = 'Sells goods or services',
  COLLECTS_RENT_FROM_TENANTS = 'Collects rent from tenants',
  SELLS_SOLAR_GENERATED_POWER_TO_THIRD_PARTIES = 'Sells solar-generated power to third-parties',
}

export enum NON_PROFIT_ORGANIZATION_TYPES {
  FBO = 'Faith-based organization',
  HOA = "Homeowner's association",
  EDU = 'Educational institution',
  OTHER = 'Other',
}

export enum INDUSTRY_BORROWER_REPORTED_TYPES {
  AGRICULTURE = 'Agriculture',
  ENERGY_UTILITIES = 'Energy & Utilities',
  ENTERTAINMENT_RECREATION = 'Entertainment & Recreation',
  FB_RESTAURANTS_BARS = 'Food & Beverage - Restaurants and Bars',
  FB_OTHER = 'Food & Beverage - Other',
  GYM_FITNESS = 'Gym & Fitness',
  HEALTHCARE = 'Healthcare',
  HOSPITALITY = 'Hospitality',
  MANUFACTURING_INDUSTRIAL_CONSTRUCTION = 'Manufacturing, Industrial, & Construction',
  MINING_OIL_GAS = 'Mining, Oil, & Gas',
  NON_PROFIT = 'Non-Profit',
  PROFESSIONAL_SERVICES = 'Professional Services',
  PUBLIC_ADMINISTRATION = 'Public Administration',
  REAL_ESTATE = 'Real Estate',
  RETAIL_CONSUMER_GOODS_SERVICES = 'Retail & Consumer Goods & Services',
  TECHNOLOGY_MEDIA_TELECOMMUNICATIONS = 'Technology, Media, & Telecommunications',
  TRANSPORTATION_WAREHOUSING = 'Transportation & Warehousing',
  WHOLESALE_GOODS = 'Wholesale Goods',
  OTHER = 'Other',
}

export const INDUSTRY_PG_TYPES = [
  INDUSTRY_BORROWER_REPORTED_TYPES.AGRICULTURE,
  INDUSTRY_BORROWER_REPORTED_TYPES.FB_RESTAURANTS_BARS,
  INDUSTRY_BORROWER_REPORTED_TYPES.GYM_FITNESS,
  INDUSTRY_BORROWER_REPORTED_TYPES.HOSPITALITY,
];

export const FOR_PROFIT_COMPANY_CHOICES = [
  {
    label: 'Sells goods or services',
    value: FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES,
  },
  {
    label: 'Collects rent from tenants',
    value: FOR_PROFIT_COMPANY_TYPES.COLLECTS_RENT_FROM_TENANTS,
  },
  {
    label: 'Sells solar-generated power to third-parties',
    value:
      FOR_PROFIT_COMPANY_TYPES.SELLS_SOLAR_GENERATED_POWER_TO_THIRD_PARTIES,
  },
];

export const NON_PROFIT_ORGANIZATION_TYPES_CHOICES = [
  { label: 'Faith-based organization', value: 'Faith-based organization' },
  { label: "Homeowner's association", value: "Homeowner's association" },
  { label: 'Educational institution', value: 'Educational institution' },
  { label: 'Other', value: 'Other' },
];

export const INDUSTRY_BORROWER_REPORTED = [
  { label: 'Agriculture', value: 'Agriculture' },
  { label: 'Energy & Utilities', value: 'Energy & Utilities' },
  { label: 'Entertainment & Recreation', value: 'Entertainment & Recreation' },
  {
    label: 'Food & Beverage - Restaurants and Bars',
    value: 'Food & Beverage - Restaurants and Bars',
  },
  { label: 'Food & Beverage - Other', value: 'Food & Beverage - Other' },
  { label: 'Gym & Fitness', value: 'Gym & Fitness' },
  { label: 'Healthcare', value: 'Healthcare' },
  { label: 'Hospitality', value: 'Hospitality' },
  {
    label: 'Manufacturing, Industrial, & Construction',
    value: 'Manufacturing, Industrial, & Construction',
  },
  { label: 'Mining, Oil, & Gas', value: 'Mining, Oil, & Gas' },
  { label: 'Non-Profit', value: 'Non-Profit' },
  { label: 'Professional Services', value: 'Professional Services' },
  { label: 'Public Administration', value: 'Public Administration' },
  { label: 'Real Estate', value: 'Real Estate' },
  {
    label: 'Retail & Consumer Goods & Services',
    value: 'Retail & Consumer Goods & Services',
  },
  {
    label: 'Technology, Media, & Telecommunications',
    value: 'Technology, Media, & Telecommunications',
  },
  {
    label: 'Transportation & Warehousing',
    value: 'Transportation & Warehousing',
  },
  { label: 'Wholesale Goods', value: 'Wholesale Goods' },
  { label: 'Other', value: 'Other' },
];

export const BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT_OPTIONS = [
  { label: 'For-Profit', value: 'For-Profit' },
  { label: 'Non-Profit', value: 'Non-Profit' },
];

export enum BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT {
  FOR_PROFIT = 'For-Profit',
  NON_PROFIT = 'Non-Profit',
}

export const CHANGE_ORDER_DOCUMENT_TYPES = {
  CHANGE_ORDER_CONTRACT: 'Change Order Contract',
  SOLAR_LOAN_PROPOSAL: 'Solar Loan Proposal',
};

export const PG_FOR_PROFIT_COMPANY_TYPES = [
  FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES,
  FOR_PROFIT_COMPANY_TYPES.COLLECTS_RENT_FROM_TENANTS,
];

export enum PROPERTY_COMPANY_UPLOAD_RENT_ROLL_OR_PROVIDE_TENANT_INFO {
  UPLOAD_RENT_ROLL = 'Upload rent roll',
  ENTER_INFORMATION_FOR_SINGLE_TENANT = 'Enter information for a single tenant',
}

export enum PROPERTY_OWNERSHIP_STATUS {
  OWNED = 'OWNED',
  LEASED = 'LEASED',
}

export type BUSINESS_OWNERSHIP_STATUS =
  | 'Minority-owned business'
  | 'Women-owned business'
  | 'LGBTQI+-owned business'
  | 'None of these apply'
  | 'I do not wish to provide this information';

export const BUSINESS_OWNERSHIP_STATUS_OPTIONS = [
  { value: 'Minority-owned business', label: 'Minority-owned business' },
  { value: 'Women-owned business', label: 'Women-owned business' },
  { value: 'LGBTQI+-owned business', label: 'LGBTQI+-owned business' },
  { value: 'None of these apply', label: 'None of these apply' },
  {
    value: 'I do not wish to provide this information',
    label: 'I do not wish to provide this information',
  },
];

export const BUSINESS_OWNERSHIP_STATUS_EXCLUSIVE_OPTIONS = [
  'None of these apply',
  'I do not wish to provide this information',
];

export const NUMBER_OF_EMPLOYEES_RANGE_OPTIONS = [
  { value: '0', label: '0' },
  { value: '1 to 4', label: '1 to 4' },
  { value: '5 to 9', label: '5 to 9' },
  { value: '10 to 19', label: '10 to 19' },
  { value: '20 to 49', label: '20 to 49' },
  { value: '50 to 99', label: '50 to 99' },
  { value: '100 to 249', label: '100 to 249' },
  { value: '250 to 499', label: '250 to 499' },
  { value: '500 or more', label: '500 or more' },
  {
    value: 'I do not wish to provide this information',
    label: 'I do not wish to provide this information',
  },
];

export const ETHNICITY = {
  HISPANIC_OR_LATINO: 'Hispanic or Latino',
  NOT_HISPANIC_OR_LATINO: 'Not Hispanic or Latino',
  DO_NOT_WISH_TO_PROVIDE: 'I do not wish to provide my ethnicity',
} as const;

export const SUB_ETHNICITY = {
  CUBAN: 'Cuban',
  MEXICAN: 'Mexican',
  PUERTO_RICAN: 'Puerto Rican',
  OTHER: 'Other',
} as const;

export const ETHNICITY_OPTIONS = Object.values(ETHNICITY).map((value) => ({
  value,
  label: value,
}));

export const SUB_ETHNICITY_OPTIONS = Object.values(SUB_ETHNICITY).map(
  (value) => ({
    value,
    label: value,
  })
);

export const ETHNICITY_OPTIONS_EXCLUSIVE = [ETHNICITY.DO_NOT_WISH_TO_PROVIDE];

export const RACE = {
  AIAN: 'American Indian or Alaska Native',
  ASIAN: 'Asian',
  BAA: 'Black or African American',
  NHPI: 'Native Hawaiian or Other Pacific Islander',
  WHITE: 'White',
  DO_NOT_WISH_TO_PROVIDE: 'I do not wish to provide my race',
} as const;

export const RACE_OPTIONS_EXCLUSIVE = [RACE.DO_NOT_WISH_TO_PROVIDE];

export const SUB_RACE_OPTIONS = {
  Asian: {
    ASIAN_INDIAN: 'Asian Indian',
    CHINESE: 'Chinese',
    FILIPINO: 'Filipino',
    JAPANESE: 'Japanese',
    KOREAN: 'Korean',
    VIETNAMESE: 'Vietnamese',
    OTHER: 'Other Asian',
  },
  'Black or African American': {
    AFRICAN_AMERICAN: 'African American',
    ETHIOPIAN: 'Ethiopian',
    HAITIAN: 'Haitian',
    JAMAICAN: 'Jamaican',
    NIGERIAN: 'Nigerian',
    SOMALI: 'Somali',
    OTHER: 'Other Black or African American',
  },
  'Native Hawaiian or Other Pacific Islander': {
    GUAMANIAN_OR_CHAMORRO: 'Guamanian or Chamorro',
    NATIVE_HAWAIIAN: 'Native Hawaiian',
    SAMOAN: 'Samoan',
    OTHER: 'Other Pacific Islander',
  },
} as const;

export const RACE_OPTIONS = Object.values(RACE).map((value) => ({
  value,
  label: value,
}));

export const OTHER_RACE_TOOLTIPS = {
  Asian:
    'Please specify your race, for example, Cambodian, Hmong, Laotian, Pakistani, Thai, and so on',
  'Black or African American':
    'Please specify your race, for example, Barbadian, Ghanaian, South African, and so on',
  'Native Hawaiian or Other Pacific Islander':
    'Please specify your race, for example, Fijian, Tongan, and so on',
} as const;

export const OTHER_RACE_LABELS = {
  'American Indian or Alaska Native':
    'Please specify the name of your enrolled or principal tribe:',
} as const;

export enum RELATED_PARTY_RELATIONSHIP {
  LANDLORD = 'Landlord',
  PRINCIPAL = 'Principal',
  GUARANTOR = 'Guarantor',
  OFFTOKER = 'Offtaker',
}

export const ACCOUNTS_TO_DISPLAY_PAYMENTS_FROM = [
  AccountRecordType.INSTALLER,
  AccountRecordType.VENDORS_DISTRIBUTORS,
];

export enum FORCE_TO {
  WELCOME = 'welcome',
  WELCOME_WITH_CR_NOTIFICATION = 'welcome_with_cr_notification',
  CLEAR_FORCE_TO = 'clear_force_to',
}

export enum FEATURE_FLAGS_MULTI {
  SHOW_PLAID = 'show_plaid',
  USE_OLD_BUSINESS_ESTABLISHED_YEAR = 'use_old_business_established_year',
}

export type ClauseOp =
  | 'EQ'
  | 'NEQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'IN'
  | 'NIN';
export type LogicOp = 'AND' | 'OR';
export type RuleType = 'GENERAL' | 'OFFRAMP';

export enum EnumFeatureType {
  PG = 'PG',
  CG = 'CG',
}

export type FeatureType = EnumFeatureType.PG | EnumFeatureType.CG;

export const BEGIN_INSTALLATION_EXPECTED_OPTIONS = [
  { label: 'As soon as possible', value: 'As soon as possible' },
  { label: 'Within the next 3-months', value: 'Within the next 3-months' },
  { label: 'Within the next 6-months', value: 'Within the next 6-months' },
  { label: 'Sometime in the next year', value: 'Sometime in the next year' },
  {
    label: 'I am still evaluating whether to move forward with the project',
    value: 'I am still evaluating whether to move forward with the project',
  },
];

export const PRIMARY_MOTIVATION_FOR_SOLAR_OPTIONS = [
  { label: 'Utility Bill Savings', value: 'Utility Bill Savings' },
  { label: 'Energy Resilience', value: 'Energy Resilience' },
  { label: 'Environmental', value: 'Environmental' },
  {
    label: 'Tax Credits or Other Incentives',
    value: 'Tax Credits or Other Incentives',
  },
  { label: 'Other', value: 'Other' },
];

export enum DEBT_OBLIGATIONS_SELECT_OPTIONS {
  DEBT_SCHEDULE = 'Debt Schedule',
  LIST_OUT_DEBT_OBLIGATIONS = 'List out debt obligations',
}

export const DEFAULT_BORROWER_ADVANCE_RATE = 13.25;

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const FOUR_YEAR_TERM_WARNING_MESSAGE =
  'No payments required for the first 12 months. Interest accrues for the first 12 months at the defined interest rate. If the balance is paid off in full by the end of month 12, interest is waived. If the balance is not paid off, automatic payments will begin sufficient to pay the loan off over a 3 year period with the defined interest rate';
